import * as moment from 'moment-timezone';

import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { RECURRENCE_FREQUENCY_TYPE, Recurrence, DAYS } from '../models/event';

@Injectable({
  providedIn: 'root',
})
export class EventRecurrenceRepeatLanguageService {
  translatedDaysOfWeek: string[] = [];
  pluralLocalizedDaysMap: Record<string, string> = {};
  NUMBER_TO_DEGREE: Record<string, string> = {};

  constructor(private translate: TranslateService) {
    this.initializeTranslations();
  }

  initializeTranslations() {
    this.translatedDaysOfWeek = [
      this.translate.instant('Sunday'),
      this.translate.instant('Monday'),
      this.translate.instant('Tuesday'),
      this.translate.instant('Wednesday'),
      this.translate.instant('Thursday'),
      this.translate.instant('Friday'),
      this.translate.instant('Saturday'),
    ];

    this.pluralLocalizedDaysMap = {
      Sundays: this.translate.instant('Sundays'),
      Mondays: this.translate.instant('Mondays'),
      Tuesdays: this.translate.instant('Tuesdays'),
      Wednesdays: this.translate.instant('Wednesdays'),
      Thursdays: this.translate.instant('Thursdays'),
      Fridays: this.translate.instant('Fridays'),
      Saturdays: this.translate.instant('Saturdays'),
    };

    this.NUMBER_TO_DEGREE = {
      '1': this.translate.instant('first'),
      '2': this.translate.instant('second'),
      '3': this.translate.instant('third'),
      '4': this.translate.instant('fourth'),
      '5': this.translate.instant('fifth'),
    };
  }

  ordinalSuffix(i: number) {
    const j = i % 10;
    const k = i % 100;
    if (j === 1 && k !== 11) {
      return `${i}st`;
    }

    if (j === 2 && k !== 12) {
      return `${i}nd`;
    }

    if (j === 3 && k !== 13) {
      return `${i}rd`;
    }

    return `${i}th`;
  }

  getRepeatLanguage(recurrence: Recurrence, recurrenceEnd?: string | Date): string {
    if (!recurrence) {
      return '';
    }


    const totalOccurrences = recurrence.totalOccurrences
      ? `, ${recurrence.totalOccurrences} ${this.translate.instant('times')}`
      : '';

    let text = '';
    switch (recurrence?.frequencyType) {
      case RECURRENCE_FREQUENCY_TYPE.DAYS: {
        const isDailyFrequency = recurrence.frequency === 1;
        text = isDailyFrequency
          ? `${this.translate.instant('Repeats daily')}${totalOccurrences}`
          : `${this.translate.instant('Every')} ${recurrence.frequency} ${this.translate.instant(
              'days',
            )}${totalOccurrences}`;
        break;
      }

      case RECURRENCE_FREQUENCY_TYPE.MONTHS: {
        const isMonthlyFrequency = recurrence.frequency === 1;
        if (recurrence.times) {
          const ordinalDay = this.ordinalSuffix(recurrence.times[0]);

          text = isMonthlyFrequency
            ? `${this.translate.instant('Repeats monthly on the')} ${ordinalDay}${totalOccurrences}`
            : `${this.translate.instant('Every')} ${recurrence.frequency} ${this.translate.instant(
                'months on the',
              )} ${ordinalDay}${totalOccurrences}`;
        } else {
          const monthlyTimeValues = recurrence.timesMonthly?.split(':');
          if (!monthlyTimeValues) {
            return '';
          }

          const weekdayOrdinal = this.NUMBER_TO_DEGREE[monthlyTimeValues[0]];
          const weekday = moment().day(monthlyTimeValues[1]).format('dddd');

          text = isMonthlyFrequency
            ? `${this.translate.instant(
                'Repeats monthly on the',
              )} ${weekdayOrdinal} ${weekday}${totalOccurrences}`
            : `${this.translate.instant('Every')} ${recurrence.frequency} ${this.translate.instant(
                'months on the',
              )} ${weekdayOrdinal} ${weekday}${totalOccurrences}`;
        }
        break;
      }

      case RECURRENCE_FREQUENCY_TYPE.WEEKS: {
        if (!recurrence.times) {
          return '';
        }

        recurrence.times.sort();
        const days = recurrence.times.map((time) => this.translatedDaysOfWeek[time]);
        const daysInEnglish = recurrence.times.map((time) => DAYS[time]);
        const lastDay = days.length > 1 && days.pop();
        const daysString = lastDay
          ? `${days.join(', ')} ${this.translate.instant('and')} ${lastDay}`
          : `${this.pluralLocalizedDaysMap[`${daysInEnglish[0]}s`]}`;

        const onWeekdays = [1, 2, 3, 4, 5].every((time) => recurrence.times.includes(time));

        const isWeeklyFrequency = recurrence.frequency === 1;

        if (onWeekdays) {
          text = `${this.translate.instant('Repeats on weekdays')}${totalOccurrences}`;
        } else {
          text = isWeeklyFrequency
            ? `${this.translate.instant('Repeats weekly on')} ${daysString}${totalOccurrences}`
            : `${this.translate.instant('Every')} ${recurrence.frequency} ${this.translate.instant(
                'weeks on',
              )} ${daysString}${totalOccurrences}`;
        }

        break;
      }

      case RECURRENCE_FREQUENCY_TYPE.YEARS: {
        const isYearlyFrequency = recurrence.frequency === 1;
        text = isYearlyFrequency
          ? `${this.translate.instant('Repeats yearly')}${totalOccurrences}`
          : `${this.translate.instant('Every')} ${recurrence.frequency} ${this.translate.instant(
              'years',
            )}${totalOccurrences}`;
        break;
      }

      default: {
        text = '';
      }
    }

    if (recurrenceEnd && !recurrence.totalOccurrences) {
      text = `${text}, ${this.translate.instant('until')} ${moment(recurrenceEnd).format(
        'MMMM Do, YYYY',
      )}`;
    }

    return text;
  }
}
